/**
 * DEFCAT-R2-15 — Structure tab (auto-derived, label-only).
 *
 * Levels are DERIVED from the actual trees (decision OQ-R2-2a): the location
 * axis = L0 (always) + the catalogue location depth; the type axis = its depth
 * (a flat type tree shows exactly ONE level — no phantom "Type 1"). "Add level"
 * and row-delete are GONE; the only editable input per derived level is its
 * LABEL, persisted via PUT axis-levels. These labels caption the Trees-tab
 * levels and feed the level-aware Add buttons (R2-11) + the drawer dropdowns.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type {
  CatalogueAxis,
  CatalogueAxisLevel,
  GetDefectCatalogueV2Response,
} from "@aeriosiq/types";
import { usePutAxisLevelsMutation } from "./taxonomyQueries.js";
import { deriveAxisLevels, type DerivedLevel } from "./structureDerive.js";

export interface StructureTabProps {
  catalogueId: string;
  versionId: string;
  structure: GetDefectCatalogueV2Response;
  editable: boolean;
}

/** axis:level → user-edited label override (empty string = use derived). */
type LabelEdits = Record<string, string>;

function editKey(axis: CatalogueAxis, level: number): string {
  return `${axis}:${String(level)}`;
}

export function StructureTab({
  catalogueId,
  versionId,
  structure,
  editable,
}: StructureTabProps): ReactElement {
  const derived = useMemo<DerivedLevel[]>(
    () => deriveAxisLevels(structure.entries, structure.axisLevels),
    [structure.entries, structure.axisLevels],
  );

  // Editable label per derived slot, seeded from the derived (saved-or-default)
  // labels so the inputs are pre-populated and re-seed when the version changes.
  const [labels, setLabels] = useState<LabelEdits>(() => seedLabels(derived));
  const put = usePutAxisLevelsMutation(catalogueId, versionId);

  useEffect(() => {
    setLabels(seedLabels(derived));
  }, [derived]);

  function setLabel(axis: CatalogueAxis, level: number, value: string): void {
    setLabels((prev) => ({ ...prev, [editKey(axis, level)]: value }));
  }

  function handleSave(): void {
    const levels: CatalogueAxisLevel[] = derived
      .map((d) => {
        const label = (labels[editKey(d.axis, d.level)] ?? d.label).trim();
        return { axis: d.axis, level: d.level, label: { en: label } };
      })
      .filter((l) => l.label.en.length > 0);
    put.mutate({ levels });
  }

  const locationLevels = derived.filter((d) => d.axis === "location");
  const typeLevels = derived.filter((d) => d.axis === "type");

  return (
    <Box sx={{ p: 1, maxWidth: 760 }}>
      <Stack spacing={3}>
        <Typography variant="body2" color="text.secondary">
          The structure is derived automatically from the trees — depth is a
          fact, not an input. Name each level; the labels caption the Trees tab,
          the Add buttons, and the defect-drawer dropdowns.
        </Typography>

        <AxisLevelTable
          title="Location axis"
          axis="location"
          levels={locationLevels}
          labels={labels}
          onLabel={setLabel}
          editable={editable}
        />
        <AxisLevelTable
          title="Type axis"
          axis="type"
          levels={typeLevels}
          labels={labels}
          onLabel={setLabel}
          editable={editable}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!editable || put.isPending}
          >
            Save structure
          </Button>
          {put.isSuccess ? (
            <Typography color="success.main" variant="body2">
              Saved
            </Typography>
          ) : null}
          {put.isError ? (
            <Typography color="error" variant="body2">
              Save failed
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

function AxisLevelTable({
  title,
  axis,
  levels,
  labels,
  onLabel,
  editable,
}: {
  title: string;
  axis: CatalogueAxis;
  levels: DerivedLevel[];
  labels: LabelEdits;
  onLabel: (axis: CatalogueAxis, level: number, value: string) => void;
  editable: boolean;
}): ReactElement {
  if (levels.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No levels yet — add nodes in the Trees tab.
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Table size="small" aria-label={`${title} level labels`}>
        <TableHead>
          <TableRow>
            <TableCell>Level</TableCell>
            <TableCell>Label</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {levels.map((row) => (
            <TableRow key={`${axis}-${String(row.level)}`}>
              <TableCell sx={{ width: 88, color: "text.secondary" }}>
                {axis === "location" && row.level === 0
                  ? "L0"
                  : `L${String(row.level)}`}
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={labels[editKey(axis, row.level)] ?? row.label}
                  onChange={(e) => {
                    onLabel(axis, row.level, e.target.value);
                  }}
                  fullWidth
                  disabled={!editable}
                  placeholder={row.label}
                  slotProps={{
                    htmlInput: {
                      "aria-label": `${title} level ${String(row.level)} label`,
                    },
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function seedLabels(derived: DerivedLevel[]): LabelEdits {
  const out: LabelEdits = {};
  for (const d of derived) out[editKey(d.axis, d.level)] = d.label;
  return out;
}
